import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Plus, Trash2, Download, Search, Filter, ArrowUpDown, ArrowUp, ArrowDown, Box } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { useSiteStore } from '../../store/siteStore';
import { useSitesStore } from '../../store/sitesStore';
import { Site } from '../../types/site';

interface SitesListViewProps {
  onSiteClick?: (site: Site) => void;
  onJobClick?: (site: Site, jobId: string) => void;
}

const SitesListView: React.FC<SitesListViewProps> = ({ onSiteClick, onJobClick }) => {
  const { 
    currentProject,
    getCurrentDesign,
    appTheme
  } = useProjectStore();
  const { sites, deleteSites } = useSitesStore();
  const isDarkTheme = appTheme === 'dark';
  
  const { loadSite } = useSiteStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSites, setSelectedSites] = useState<Set<string>>(new Set());
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [expandedJobBadge, setExpandedJobBadge] = useState<string | null>(null);
  const [badgePosition, setBadgePosition] = useState<{ left: number; top: number } | null>(null);
  const expandedBadgeRef = useRef<HTMLDivElement>(null);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const prevSortRef = useRef<{ column: string | null; direction: 'asc' | 'desc' }>({ column: null, direction: 'asc' });

  const [hoveredSiteId, setHoveredSiteId] = useState<string | null>(null);
  const filteredSites = useMemo(() => {
    let result = sites;
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(site => 
        site.name.toLowerCase().includes(query) ||
        site.location.toLowerCase().includes(query)
      );
    }
    
    // Apply sorting
    if (sortColumn) {
      result = [...result].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        
        switch (sortColumn) {
          case 'name':
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
            break;
          case 'location':
            aValue = a.location.toLowerCase();
            bValue = b.location.toLowerCase();
            break;
          case 'towerHeight':
            aValue = a.tower.height;
            bValue = b.tower.height;
            break;
          case 'equipmentCount':
            // Count equipment from the 3D view/designer
            const currentDesign = getCurrentDesign();
            const getDesignEquipmentCount = (site: Site) => {
              if (!currentDesign) return 0;
              const isAssociated = !site.networkDesigns || 
                                site.networkDesigns.length === 0 || 
                                site.networkDesigns.includes(currentDesign.id);
              return isAssociated ? (currentDesign.equipment?.length || 0) : 0;
            };
            aValue = getDesignEquipmentCount(a);
            bValue = getDesignEquipmentCount(b);
            break;
          case 'pendingJobs':
            aValue = (a.jobs || []).filter(job => job.status === 'pending').length;
            bValue = (b.jobs || []).filter(job => job.status === 'pending').length;
            break;
          case 'lastUpdated':
            aValue = a.updatedAt instanceof Date ? a.updatedAt.getTime() : new Date(a.updatedAt).getTime();
            bValue = b.updatedAt instanceof Date ? b.updatedAt.getTime() : new Date(b.updatedAt).getTime();
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return result;
  }, [sites, searchQuery, sortColumn, sortDirection]);
  
  const handleSort = (column: string) => {
    const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
    const newColumn = sortColumn === column ? sortColumn : column;
    
    // Check if sort actually changed
    const sortChanged = prevSortRef.current.column !== newColumn || prevSortRef.current.direction !== newDirection;
    
    if (sortChanged) {
      setShouldAnimate(true);
      prevSortRef.current = { column: newColumn, direction: newDirection };
      
      // Reset animation flag after animation completes
      setTimeout(() => setShouldAnimate(false), 500);
    }
    
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  const getSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return null;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp size={12} className="text-gray-300" />
      : <ArrowDown size={12} className="text-gray-300" />;
  };

  const formatDate = (date: Date | string) => {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) return 'Invalid date';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(dateObj);
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedSites(new Set(filteredSites.map(site => site.id)));
    } else {
      setSelectedSites(new Set());
    }
  };

  const handleSelectSite = (siteId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const newSelected = new Set(selectedSites);
    if (e.target.checked) {
      newSelected.add(siteId);
    } else {
      newSelected.delete(siteId);
    }
    setSelectedSites(newSelected);
  };

  const handleDeleteSelected = () => {
    if (selectedSites.size === 0 || !currentProject) return;
    
    const count = selectedSites.size;
    const message = count === 1 
      ? 'Are you sure you want to delete this site? This action cannot be undone.'
      : `Are you sure you want to delete ${count} sites? This action cannot be undone.`;
    
    if (window.confirm(message)) {
      void deleteSites(Array.from(selectedSites));
      setSelectedSites(new Set());
    }
  };

  const handleDeleteSite = (siteId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (window.confirm('Are you sure you want to delete this site? This action cannot be undone.')) {
      void deleteSites([siteId]);
    }
  };

  const handleSiteClick = (site: Site) => {
    if (onSiteClick) {
      onSiteClick(site);
    }
  };

  const handleRefresh = () => {
    if (currentProject?.id) {
      void useSitesStore.getState().loadForCollection(currentProject.id);
    }
  };

  const isAllSelected = filteredSites.length > 0 && selectedSites.size === filteredSites.length;
  const isIndeterminate = selectedSites.size > 0 && selectedSites.size < filteredSites.length;

  // Close expanded badge when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      // Check if click is outside both the badge button and the expanded list
      if (expandedBadgeRef.current && !expandedBadgeRef.current.contains(target)) {
        // Also check if clicking on the expanded list itself (should not close)
        const expandedList = document.querySelector('[data-expanded-job-list]');
        if (!expandedList || !expandedList.contains(target)) {
          setExpandedJobBadge(null);
          setBadgePosition(null);
        }
      }
    };

    if (expandedJobBadge) {
      // Use a small delay to allow click events to complete first
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside, true);
      }, 250);
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside, true);
      };
    }
  }, [expandedJobBadge]);

  // Show empty state if no project
  if (!currentProject) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="text-6xl mb-4">📡</div>
          <h3 className="text-lg font-medium text-gray-300 mb-2">No Collection Loaded</h3>
          <p className="text-gray-400">Please load or create a collection to manage sites</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex-1 flex flex-col overflow-hidden ${isDarkTheme ? 'bg-[#171a1f]' : 'bg-gray-900'}`}>
      {/* Header Section */}
      <div className={`${isDarkTheme ? 'bg-[#20242b] border-b border-[#343a43]' : 'bg-gray-800 border-b border-gray-700'} px-4 py-2`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-white">Site Manager</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">
              Total ({filteredSites.length})
            </span>
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="p-1.5 hover:bg-gray-700 rounded transition-colors"
              title="Filter"
            >
            </button>
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => window.dispatchEvent(new Event('openSiteModal'))}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-gray-200 rounded hover:bg-gray-600 transition-colors text-xs font-medium"
          >
            <Plus size={14} />
            Add
          </button>
          
          <button
            onClick={handleDeleteSelected}
            disabled={selectedSites.size === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-700 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors text-xs font-medium"
          >
            <Trash2 size={14} />
            Delete
          </button>

          <button
            onClick={() => {}}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 text-gray-200 rounded hover:bg-gray-600 transition-colors text-xs font-medium"
            title="Export (Coming soon)"
          >
            <Download size={14} />
            Export
          </button>

          {/* Search Bar */}
          <div className="flex-1 max-w-xs ml-auto">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
              <input
                type="text"
                placeholder="Search sites..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-gray-700 border border-gray-600 rounded text-xs text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="flex-1 overflow-auto custom-scrollbar">
        {filteredSites.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="text-gray-400 mb-4">
                {sites.length === 0 ? (
                  <>
                    <div className="text-6xl mb-4">📡</div>
                    <h3 className="text-lg font-medium text-gray-300 mb-2">No Sites Found</h3>
                    <p className="text-gray-500 mb-4">Create your first site to get started</p>
                    <button
                      onClick={() => window.dispatchEvent(new Event('openSiteModal'))}
                      className={`${isDarkTheme ? 'px-6 py-2 bg-[#353c46] text-gray-100 rounded hover:bg-[#3e4652] transition-colors' : 'px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors'}`}
                    >
                      Create New Site
                    </button>
                  </>
                ) : (
                  <>
                    <Search size={48} className="mx-auto mb-4 text-gray-600" />
                    <p className="text-gray-400">No sites found matching "{searchQuery}"</p>
                  </>
                )}
              </div>
            </div>
          </div>
        ) : (
          <table className="w-full" style={{ borderCollapse: 'collapse' }}>
              <thead className={`${isDarkTheme ? 'bg-[#252a32]' : 'bg-gray-800'} sticky top-0 z-10`}>
                <tr>
                  <th className="px-3 py-2 text-left border-b border-gray-700">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isIndeterminate;
                      }}
                      onChange={handleSelectAll}
                      className="w-3.5 h-3.5 text-blue-600 border-gray-500 rounded bg-gray-700 focus:ring-blue-500"
                    />
                  </th>
                  <th 
                    className={`px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-700 cursor-pointer transition-colors ${isDarkTheme ? 'hover:bg-[#2d333c] hover:text-gray-200' : 'hover:bg-gray-700 hover:text-gray-300'}`}
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Name
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th 
                    className={`px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-700 cursor-pointer transition-colors ${isDarkTheme ? 'hover:bg-[#2d333c] hover:text-gray-200' : 'hover:bg-gray-700 hover:text-gray-300'}`}
                    onClick={() => handleSort('location')}
                  >
                    <div className="flex items-center gap-1">
                      Location
                      {getSortIcon('location')}
                    </div>
                  </th>
                  <th 
                    className={`px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-700 cursor-pointer transition-colors ${isDarkTheme ? 'hover:bg-[#2d333c] hover:text-gray-200' : 'hover:bg-gray-700 hover:text-gray-300'}`}
                    onClick={() => handleSort('towerHeight')}
                  >
                    <div className="flex items-center gap-1">
                      Tower Height
                      {getSortIcon('towerHeight')}
                    </div>
                  </th>
                  <th 
                    className={`px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-700 cursor-pointer transition-colors ${isDarkTheme ? 'hover:bg-[#2d333c] hover:text-gray-200' : 'hover:bg-gray-700 hover:text-gray-300'}`}
                    onClick={() => handleSort('equipmentCount')}
                  >
                    <div className="flex items-center gap-1">
                      Equipment Count
                      {getSortIcon('equipmentCount')}
                    </div>
                  </th>
                  <th 
                    className={`px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-700 cursor-pointer transition-colors ${isDarkTheme ? 'hover:bg-[#2d333c] hover:text-gray-200' : 'hover:bg-gray-700 hover:text-gray-300'}`}
                    onClick={() => handleSort('pendingJobs')}
                  >
                    <div className="flex items-center gap-1">
                      Pending Jobs
                      {getSortIcon('pendingJobs')}
                    </div>
                  </th>
                  <th 
                    className={`px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wide border-b border-gray-700 cursor-pointer transition-colors ${isDarkTheme ? 'hover:bg-[#2d333c] hover:text-gray-200' : 'hover:bg-gray-700 hover:text-gray-300'}`}
                    onClick={() => handleSort('lastUpdated')}
                  >
                    <div className="flex items-center gap-1">
                      Last Updated
                      {getSortIcon('lastUpdated')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredSites.map((site, index) => {
                  const isSelected = selectedSites.has(site.id);
                  
                  return (
                    <tr
                      key={site.id}
                      onClick={() => handleSiteClick(site)}
                      onMouseEnter={() => setHoveredSiteId(site.id)}
                      onMouseLeave={() => setHoveredSiteId(null)}
                      className={`hover:bg-gray-800 cursor-pointer transition-colors ${isSelected ? 'bg-gray-800' : ''}`}
                      style={{ 
                        ...(shouldAnimate && {
                          transition: 'all 0.5s ease-in-out',
                          transitionDelay: `${index * 20}ms`,
                          animation: 'fadeInUp 0.5s ease-out'
                        })
                      }}
                    >
                      <td className="px-3 py-1.5" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => handleSelectSite(site.id, e)}
                          className="w-3.5 h-3.5 text-blue-600 border-gray-500 rounded bg-gray-700 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-white">{site.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="text-sm text-gray-300">{site.location}</span>
                      </td>
                      <td className="px-3 py-1.5">
                        <span className="text-sm text-gray-300">{site.tower.height}m</span>
                      </td>
                      <td className="px-3 py-1.5">
                        {(() => {
                          // Count equipment from the 3D view/designer
                          const currentDesign = getCurrentDesign();
                          let equipmentCount = 0;
                          
                          if (currentDesign) {
                            // Check if site is associated with this design
                            const isAssociated = !site.networkDesigns || 
                                              site.networkDesigns.length === 0 || 
                                              site.networkDesigns.includes(currentDesign.id);
                            
                            if (isAssociated) {
                              // Count equipment from the current design
                              equipmentCount = currentDesign.equipment?.length || 0;
                            }
                          }
                          
                          return (
                            <span className="text-sm text-gray-300">{equipmentCount}</span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-1.5 relative">
                        {(() => {
                          const pendingJobsList = (site.jobs || []).filter(job => {
                            // Ensure dates are Date objects
                            const jobDate = job.createdAt instanceof Date ? job.createdAt : new Date(job.createdAt);
                            return job.status === 'pending' && !isNaN(jobDate.getTime());
                          });
                          const pendingJobs = pendingJobsList.length;
                          const isExpanded = expandedJobBadge === site.id;

                          return pendingJobs > 0 ? (
                            <div className="relative" ref={expandedBadgeRef}>
                              <button
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!isExpanded) {
                                    // Get the button element's position directly
                                    const buttonElement = e.currentTarget;
                                    const rect = buttonElement.getBoundingClientRect();
                                    setBadgePosition({ left: rect.left, top: rect.bottom + 4 });
                                    setExpandedJobBadge(site.id);
                                  } else {
                                    setBadgePosition(null);
                                    setExpandedJobBadge(null);
                                  }
                                }}
                                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/30 transition-colors cursor-pointer"
                              >
                                {pendingJobs}
                              </button>
                              
                              {/* Expanded Job List - Using fixed positioning to escape table overflow */}
                              {isExpanded && badgePosition && (
                                <div 
                                  data-expanded-job-list
                                  className="fixed bg-yellow-900 border border-yellow-500/50 rounded-md shadow-xl min-w-[200px] max-w-[300px] overflow-hidden"
                                  style={{ 
                                    zIndex: 9999,
                                    left: badgePosition.left + 'px',
                                    top: badgePosition.top + 'px'
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="py-0.5">
                                    {pendingJobsList.map((job) => {
                                      const dueDate = job.dueDate 
                                        ? (job.dueDate instanceof Date ? job.dueDate : new Date(job.dueDate))
                                        : null;
                                      return (
                                        <button
                                          key={job.id}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (onJobClick) {
                                              onJobClick(site, job.id);
                                            }
                                            setExpandedJobBadge(null);
                                          }}
                                          className="w-full text-left px-2 py-1 hover:bg-yellow-800/50 transition-colors text-xs text-yellow-400 border-b border-yellow-700/30 last:border-b-0"
                                        >
                                          <div className="font-medium truncate">{job.title}</div>
                                          {dueDate && !isNaN(dueDate.getTime()) && (
                                            <div className="text-[10px] text-yellow-300/70 mt-0.5">
                                              Due: {dueDate.toLocaleDateString()}
                                            </div>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-700 text-gray-400 border border-gray-600">
                              0
                            </span>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-1.5 relative">
                        <span className="text-sm text-gray-400">{formatDate(site.updatedAt)}</span>
                          {hoveredSiteId === site.id && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                loadSite(site.id);
                                window.dispatchEvent(new CustomEvent('openTower3D', { detail: { siteId: site.id } }));
                              }}
                              className="absolute right-3 top-1/2 -translate-y-1/2 p-0 hover:bg-gray-700 rounded transition-colors flex items-center justify-center overflow-visible"
                              title="Open 3D View"
                              style={{ width: '20px', height: '20px' }}
                            >
                              <Box size={20} className="text-blue-400" style={{ transform: 'scale(1.15)' }} />
                            </button>
                          )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
        )}
      </div>
    </div>
  );
};

export default SitesListView;
